import type { InsightGroup } from "@editor/infra/analytics";
import type { ReactElement } from "react";
import type { Insight } from "schemas/generated/insights";

import * as React from "react";

import LogoBadge from "@common/LogoBadge";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { docs } from "@editor/utils/docs";
import { getInsightGroup } from "@editor/utils/insights";

import { InsightsCard } from "@/features/analytics/insights/InsightsCard";
import useAnalyticsInsights from "@/features/analytics/useAnalyticsInsights";
import InlineAlert from "@replo/design-system/components/alert/InlineAlert";
import Button from "@replo/design-system/components/button/Button";
import Chip from "@replo/design-system/components/chip/SelectableChip";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { endOfWeek, format, startOfWeek, subWeeks } from "date-fns";
import {
  AppWindow,
  ArrowUpRight,
  Calendar,
  Info,
  PenLine,
  RefreshCw,
  Smile,
  Sprout,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useAnalyticsQueryContext } from "../contexts/AnalyticsQueryContext";
import useAnalyticsStatus from "../useAnalyticsStatus";
import { InsightsAIMicrovacModal } from "./AnalyticsMicroVac";

/**
 * Returns the date range for the past week (i.e. the week we're showing the insights for).
 *
 * It will be in the format: "Apr 14 - Apr 20"
 *
 * @author Max 2025-04-23
 */
function getWeekDateRange() {
  const lastWeek = subWeeks(new Date(), 1);
  const lastMonday = startOfWeek(lastWeek, { weekStartsOn: 1 });
  const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });

  return `${format(lastMonday, "MMM d")} - ${format(lastSunday, "MMM d")}`;
}

const InsightsByGroupType: React.FC<{
  insights: Insight[];
  groupType: InsightGroup;
}> = ({ insights, groupType }) => {
  const { dataDisplayType } = useAnalyticsStatus();
  if (insights.length > 0) {
    return (
      <>
        {insights.slice(0, 4).map((insight, index) => (
          <InsightsCard
            key={index}
            insight={insight}
            shouldBlurContent={
              // NOTE (Max, 2025-06-17): if displayType is partialData, we only show the 1st insight for build-in-replo
              dataDisplayType === "partialData" &&
              (groupType === "build-in-replo" ? index > 0 : true)
            }
          />
        ))}
      </>
    );
  }

  const { title, description, icon } = exhaustiveSwitch({ type: groupType })({
    "build-in-replo": {
      title: "No suggested edits",
      description:
        "Check back next week for new opportunities to improve your pages.",
      icon: <PenLine className="icon-size-default" />,
    },
    "trending-up": {
      title: "No big growth",
      description: "We didn’t spot any major gains this week.",
      icon: <Sprout className="icon-size-default" />,
    },
    "trending-down": {
      title: "Looking good",
      description:
        "Good news—no big drops or declines worth flagging in this week.",
      icon: <Smile className="icon-size-default" />,
    },
    other: {
      title: "No other page insights",
      description: "No other kinds of insights found.",
      icon: <AppWindow className="icon-size-default" />,
    },
  });

  return (
    <div className="border-secondary col-span-2 rounded-lg border py-30 h-[350px]">
      <EmptyState title={title} description={description} icon={icon} />
    </div>
  );
};

const AnalyticsInsights: React.FC = () => {
  const { workspaceId, projectId } = useAnalyticsQueryContext();

  const [isInsightsAIMicrovacModalOpen, setIsInsightsAIMicrovacModalOpen] =
    React.useState(false);

  const logEvent = useLogAnalytics();

  const handleFeedbackClick = () => {
    window.open(docs.insights.feedbackForm, "_blank");
    logEvent("analytics.insights.feedbackLink.click", {
      workspaceId,
      projectId,
    });
  };

  const keyPagesSubHeaderItems = [
    {
      text: getWeekDateRange(),
      icon: <Calendar size={14} />,
    },
    {
      text: "Weekly",
      icon: <RefreshCw size={14} />,
    },
  ];

  const [activeInsightGroup, setActiveInsightGroup] =
    React.useState<InsightGroup>("build-in-replo");

  const {
    allInsights,
    activeGroupInsights,
    isLoading: isInsightsLoading,
  } = useAnalyticsInsights(activeInsightGroup);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO (Ryan, 2025-08-19): This needs cleaned up, there's likely a default state being set incorrectly
  React.useEffect(() => {
    if (
      activeInsightGroup === "build-in-replo" &&
      allInsights?.[0] &&
      activeGroupInsights?.length === 0
    ) {
      const insightGroup = getInsightGroup(allInsights[0]);
      setActiveInsightGroup(insightGroup);
    }
  }, [allInsights]);

  const handleInsightGroupChange = (key: InsightGroup) => {
    setActiveInsightGroup(key);
    logEvent("analytics.insights.group.change", {
      workspaceId,
      projectId,
      newGroup: key,
    });
  };

  const insightsGroups: {
    key: InsightGroup;
    label: string;
    icon: ReactElement | null;
  }[] = [
    {
      key: "build-in-replo",
      label: "Built In Replo",
      icon: <LogoBadge aria-hidden className="h-4 w-4" />,
    },
    {
      key: "trending-up",
      label: "Trending Up",
      icon: <TrendingUp className="h-4 w-4" />,
    },
    {
      key: "trending-down",
      label: "Trending Down",
      icon: <TrendingDown className="h-4 w-4" />,
    },
    {
      key: "other",
      label: "Other",
      icon: <Info className="h-4 w-4" />,
    },
  ];

  return (
    <div className="flex justify-center overflow-auto">
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row justify-between w-full gap-2">
              <div className="flex flex-row gap-2 flex-wrap">
                {insightsGroups.map((group) => (
                  <Chip
                    key={group.key}
                    size="base"
                    active={activeInsightGroup === group.key}
                    startEnhancer={group.icon ?? undefined}
                    onClick={() => handleInsightGroupChange(group.key)}
                  >
                    {group.label}
                  </Chip>
                ))}
              </div>
              <div className="text-muted flex flex-row flex-wrap justify-start sm:justify-end gap-3 text-sm items-start">
                {keyPagesSubHeaderItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-row items-center gap-1.5"
                  >
                    {item.icon}
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {isInsightsLoading ? (
                <>
                  <Skeleton className="h-[135px] w-full" />
                  <Skeleton className="h-[135px] w-full" />
                  <Skeleton className="h-[135px] w-full" />
                  <Skeleton className="h-[135px] w-full" />
                </>
              ) : (
                <InsightsByGroupType
                  insights={activeGroupInsights ?? []}
                  groupType={activeInsightGroup}
                />
              )}
            </div>
          </div>

          <InlineAlert
            variant="neutral"
            endEnhancer={
              <Button
                variant="tertiary"
                onClick={handleFeedbackClick}
                endEnhancer={<ArrowUpRight size={14} />}
              >
                Send feedback
              </Button>
            }
          >
            New insights coming soon! Have a request or feedback? Let us know
            what would help you most
          </InlineAlert>
        </div>
        {isFeatureEnabled("debug-insights") && (
          <>
            <Button
              variant="primary"
              onClick={() => setIsInsightsAIMicrovacModalOpen(true)}
            >
              [INTERNAL] Open Insights AI Microvac
            </Button>
            <InsightsAIMicrovacModal
              isOpen={isInsightsAIMicrovacModalOpen}
              onClose={() => setIsInsightsAIMicrovacModalOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsInsights;
